import { ObservatoryDashboard } from "./features/observatory/ObservatoryDashboard";
import { useObservatoryCopy } from "./features/observatory/copy";
import { RotateCw } from "lucide-react";
import { ImportProgress } from "./ImportProgress";
import { useEffect, useState } from "react";
import {
  getPublicReporting,
  type PublicReporting,
} from "./communityReportingApi";
import { useReportingCopy } from "./helpdeskReportingCopy";
import {
  activeDateTimeFormatter,
  activeFormattingLocale,
} from "./i18n/formatting";
import "./styles/community-reporting.css";

export function CommunityThreatAnalytics() {
  const { t } = useReportingCopy();
  const { t: ot } = useObservatoryCopy();
  const [data, setData] = useState<PublicReporting | null>(null);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    setData(null);
    void getPublicReporting(controller.signal)
      .then(setData)
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => controller.abort();
  }, [revision]);
  return (
    <main className="analytics-page community-reporting">
      <section className="analytics-intro shell">
        <div>
          <span className="eyebrow">KAVOOSH COMMUNITY OBSERVATORY</span>
          <h1>{t("communityTitle")}</h1>
          <p>{ot("intro")}</p>
        </div>
        <div className="observatory-published-meta">
          {data?.state === "published" && (
            <div>
              <span>{t("publishedLabel")}</span>
              <strong>
                <time dateTime={data.published_at ?? undefined}>
                  {data.published_at
                    ? activeDateTimeFormatter(activeFormattingLocale(), {
                        dateStyle: "medium",
                      }).format(new Date(data.published_at))
                    : "—"}
                </time>
              </strong>
            </div>
          )}
          <button
            className="button secondary observatory-refresh"
            aria-label={t("retry")}
            title={t("retry")}
            disabled={!data && !error}
            onClick={() => setRevision((v) => v + 1)}
          >
            <RotateCw size={17} aria-hidden="true" />
          </button>
        </div>
      </section>
      <div className="shell">
        {error ? (
          <p role="alert">{t("unavailable")}</p>
        ) : !data ? (
          <ImportProgress label={t("loading")} />
        ) : data.state !== "published" ? (
          <p role="status">
            {t(data.state === "withdrawn" ? "withdrawn" : "empty")}
          </p>
        ) : (
          <ObservatoryDashboard
            key={data.version}
            data={data.observatory}
            showMethods={false}
            dateFilter={data.date_filter}
            loadPeriod={async (from, to, signal) => {
              try {
                const result = await getPublicReporting(signal, {
                  from_date: from,
                  to_date: to,
                  publication_version: data.version!,
                });
                if (result.state !== "published") {
                  setData(result);
                  throw new Error("Reporting publication is unavailable");
                }
                if (result.version !== data.version)
                  throw new Error("REPORTING_PUBLICATION_CHANGED");
                if (!result.observatory || !result.date_filter)
                  throw new Error("Reporting period is unavailable");
                return {
                  observatory: result.observatory,
                  date_filter: result.date_filter,
                };
              } catch (reason) {
                if (
                  reason instanceof Error &&
                  reason.message === "REPORTING_PUBLICATION_CHANGED"
                )
                  setRevision((value) => value + 1);
                throw reason;
              }
            }}
          />
        )}
      </div>
    </main>
  );
}
